import { DataTable, usePagination } from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexRow } from '@/components/ui/flex';
import {
  useAdminSourcesService,
  useDiscardedItemsService,
} from '@/services/backoffice';
import type { DiscardedSourceItemResponse } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { ExternalLink } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';

const columns: ColumnDef<DiscardedSourceItemResponse>[] = [
  {
    accessorKey: 'sourceTitle',
    header: ({ column }) => (
      <TanstackSortableColumnHeader column={column}>
        Source
      </TanstackSortableColumnHeader>
    ),
    meta: {
      headerClassName: 'min-w-[140px] max-w-[180px]',
      cellClassName: 'min-w-[140px] max-w-[180px]',
    },
    cell: ({ row }) => (
      <span className="text-sm font-medium">{row.original.sourceTitle}</span>
    ),
  },
  {
    accessorKey: 'publicationDate',
    header: ({ column }) => (
      <TanstackSortableColumnHeader column={column}>
        Date
      </TanstackSortableColumnHeader>
    ),
    meta: {
      headerClassName: 'min-w-[100px] max-w-[110px]',
      cellClassName: 'min-w-[100px] max-w-[110px]',
    },
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {dayjs(row.original.publicationDate).format('YYYY-MM-DD')}
      </span>
    ),
    sortingFn: 'datetime',
  },
  {
    accessorKey: 'url',
    header: 'Link',
    meta: {
      headerClassName: 'min-w-[60px] max-w-[70px]',
      cellClassName: 'min-w-[60px] max-w-[70px]',
    },
    cell: ({ row }) => (
      <a
        href={row.original.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
      >
        <ExternalLink className="w-3.5 h-3.5 shrink-0" />
        <span>Link</span>
      </a>
    ),
  },
  {
    accessorKey: 'reason',
    header: 'Reason',
    meta: {
      headerClassName: 'min-w-[400px]',
      cellClassName: 'min-w-[400px]',
    },
    cell: ({ row }) => (
      <span
        className="text-sm text-muted-foreground line-clamp-3"
        title={row.original.reason ?? undefined}
      >
        {row.original.reason ?? '—'}
      </span>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <TanstackSortableColumnHeader column={column}>
        Discarded at
      </TanstackSortableColumnHeader>
    ),
    meta: {
      headerClassName: 'min-w-[100px] max-w-[110px]',
      cellClassName: 'min-w-[100px] max-w-[110px]',
    },
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {dayjs(row.original.createdAt).format('YYYY-MM-DD')}
      </span>
    ),
    sortingFn: 'datetime',
  },
];

export const DiscardedItemsTab: FC = () => {
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const sourceFilter =
    selectedSources.length === 1 ? selectedSources[0] : undefined;
  const { items, isLoading } = useDiscardedItemsService(sourceFilter);
  const { sources } = useAdminSourcesService();
  const { pagination } = usePagination({ manualPagination: false });

  const sourceOptions: FilterOption[] = useMemo(
    () =>
      sources.map(s => ({
        value: s.id,
        label: s.title,
      })),
    [sources]
  );

  const filteredItems = useMemo(() => {
    if (selectedSources.length <= 1) return items;
    const set = new Set(selectedSources);
    return items.filter(item => set.has(item.sourceId));
  }, [items, selectedSources]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Discarded Items</h2>
      </div>

      <FlexRow gap="2" className="flex-wrap">
        <FilterPopoverTrigger
          label="Source"
          isActive={selectedSources.length > 0}
          activeContent={
            selectedSources.length > 0
              ? `${selectedSources.length} selected`
              : undefined
          }
          onClear={() => setSelectedSources([])}
        >
          <FilterOptionList
            options={sourceOptions}
            selected={selectedSources}
            onChange={setSelectedSources}
          />
        </FilterPopoverTrigger>
      </FlexRow>

      <DataTable
        columns={columns}
        data={filteredItems}
        isLoading={isLoading}
        pagination={pagination}
        options={{
          getPaginationRowModel: getPaginationRowModel(),
          getSortedRowModel: getSortedRowModel(),
          initialState: {
            sorting: [{ id: 'publicationDate', desc: true }],
          },
        }}
      />
    </div>
  );
};
